// Attack verb (A1, monorail-g79 — plan amoeba-environment-and-drops.md).
// LMB attacks toward the cursor, always — the verb is innate; the equipped
// weapon (weapons.ts) defines its shape. Melee lashes a corridor from the
// membrane and snag-eats small prey (closing distance pays in food); ranged
// spits a projectile (damage at range, no food — projectiles.ts owns contact).
// Axopod boosts both via mods.attackDamageMult / attackReachMult.
//
// Grew out of the Axopod power's lash system — the first aimed verb, and the
// reason WASD+cursor won the controls shootout.

import type { Entity } from '../components'
import type { GameWorld } from '../world'
import type { GameInput } from '../controls'
import type { EventBus } from '../events'
import { equippedWeaponDef } from '../weapons'
import { playerCanEat } from '../growth'
import { dnaForRadius, dropDnaForPredator, chainSegmentVulnerable, damageChainSegment } from './damage'
import { hitContainer } from './containers'
import { spawnInkSplatter, spawnPigmentPuff } from './particles'
import { playSfx, panFor } from '../../engine/audio'
import { morphologyFrom } from '../creature-recipe'
import { T } from '../tunables'
import { settings } from '../settings'

/** "of Sniffles" affix: a fraction of attack damage returns as integrity.
 * Shared by melee (here) and ranged shots (projectiles.ts). */
export function healFromLifesteal(player: Entity, damageDealt: number): void {
  const frac = player.mods?.attackLifesteal ?? 0
  if (frac <= 0 || !player.health) return
  player.health.current = Math.min(player.health.max, player.health.current + damageDealt * frac)
}

/** Distance from point P to segment A→B. */
function segmentDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const abx = bx - ax
  const aby = by - ay
  const len2 = abx * abx + aby * aby
  const t = len2 > 0 ? Math.max(0, Math.min(1, ((px - ax) * abx + (py - ay) * aby) / len2)) : 0
  const cx = ax + abx * t
  const cy = ay + aby * t
  return Math.hypot(px - cx, py - cy)
}

const LASH_ANIM_SEC = 0.30

export function attackSystem(world: GameWorld, player: Entity, input: GameInput, dt: number, events: EventBus): void {
  player.attackCooldown = Math.max(0, (player.attackCooldown ?? 0) - dt)
  if (player.lash) {
    player.lash.timer -= dt
    if (player.lash.timer <= 0) player.lash = undefined
  }

  if (player.attackCooldown > 0) return
  // aimSystem runs every frame in the 'input' stage; missing only mid-migration tests.
  if (!player.aim) return

  // Three ways to fire: a (buffered) press, holding the attack input down
  // (repeats at weapon speed), or the predation reflex — settings.autoFire
  // firing whenever the aim assist holds a soft-locked target.
  const pressed = input.actions.justPressedWithin('attack')
  const held = input.actions.held('attack')
  const reflex = settings.autoFire && player.aim.target !== undefined
  if (!pressed && !held && !reflex) return
  if (pressed) input.actions.consume('attack')

  // Cursor sitting on top of you: no direction to attack. Heading aim never
  // has this problem — dir always comes from movement/turn-rate, never the raw cursor.
  if (player.aim.source === 'cursor') {
    const dist = Math.hypot(input.mouse.x - player.position.x, input.mouse.y - player.position.y)
    if (dist < 0.05) return
  }
  const nx = player.aim.dir.x
  const ny = player.aim.dir.y

  const weapon = equippedWeaponDef(player)
  const mods = player.mods
  const reachMult = mods?.attackReachMult ?? 1
  // Flat adds before the multiplier (D2 order): (base + Jagged) × Cruel × Axopod.
  const damage = (weapon.damage + (mods?.attackDamageAdd ?? 0)) * (mods?.attackDamageMult ?? 1)
  const pr = player.renderable?.radius ?? T.player.baseRadius

  player.attackCooldown = weapon.cooldown * (mods?.attackCooldownMult ?? 1)

  if (weapon.kind === 'ranged') {
    // Muzzle puff at the membrane; the shot carries the weapon's tint.
    const mx = player.position.x + nx * pr
    const my = player.position.y + ny * pr
    world.add({
      position: { x: mx, y: my },
      velocity: { x: nx * weapon.projectileSpeed!, y: ny * weapon.projectileSpeed! },
      renderable: { shape: 'microbe', radius: weapon.projectileRadius!, tint: weapon.tint },
      // GRAIN body + velocity heading = a little teardrop in flight.
      morphology: morphologyFrom({ body: 'grain' }, Math.random()),
      // Reach boosts extend flight time — melee gets length, ranged gets range.
      projectile: { kind: 'shot', ttl: weapon.ttl! * reachMult, damage },
    })
    spawnPigmentPuff(world, mx, my, weapon.tint, 0.04)
    playSfx('dash_whoosh', { volume: 0.2 })
    return
  }

  // ── melee lash ─────────────────────────────────────────────────────────────
  const length = weapon.length! * reachMult
  const width = weapon.width!
  const ax = player.position.x + nx * pr
  const ay = player.position.y + ny * pr
  const bx = ax + nx * length
  const by = ay + ny * length

  // Whip animation state — player.slang draws the tendril from the membrane.
  player.lash = { angle: Math.atan2(ny, nx), timer: LASH_ANIM_SEC, len: length + pr }
  playSfx('dash_whoosh', { volume: 0.25 })

  // Tip splash only — the whip itself is drawn by the player shader.
  spawnPigmentPuff(world, bx, by, weapon.tint, 0.05)

  // Predators in the corridor take a hit; edible-size ones get eaten outright.
  for (const pred of [...world.with('predator', 'position', 'renderable', 'health')]) {
    if (pred.absorbing || pred.chainSegment) continue
    if (segmentDist(pred.position.x, pred.position.y, ax, ay, bx, by) > width + pred.renderable.radius) continue

    if (playerCanEat(player, pred)) {
      // Snagged prey: reel it in via the standard absorb (reversal payout).
      world.removeComponent(pred, 'predator')
      world.addComponent(pred, 'absorbing', {
        progress: 0,
        duration: 0.20,
        startRadius: pred.renderable.radius,
        startPos: { x: pred.position.x, y: pred.position.y },
        dnaValue: dnaForRadius(pred.renderable.radius),
        growthUnits: T.growth.predatorGrowthUnits,
      })
      events.emit({ type: 'predator.eaten', predator: pred })
      continue
    }

    pred.health!.current -= damage
    pred.hitFlash = T.damage.hitFlashDuration
    events.emit({ type: 'damage.dealt', target: pred, amount: damage })
    healFromLifesteal(player, damage)
    spawnInkSplatter(world, pred.position.x, pred.position.y)
    playSfx('dash_impact', { volume: 0.3, pan: panFor(pred.position.x, player.position.x) })
    if (pred.health!.current <= 0) {
      dropDnaForPredator(world, pred)
      if (world.id(pred) !== undefined) world.remove(pred)
    }
  }

  // Boss chain segments in the corridor take chip damage while vulnerable
  // (monorail-228 — weapons opened up beyond the dash). One swing iterates each
  // segment once, so no multi-hit guard is needed; chip stays below dash's
  // 0.5/hit via T.boss.chain.weaponChipMult.
  for (const seg of [...world.with('chainSegment', 'position', 'renderable', 'health')]) {
    if (!chainSegmentVulnerable(seg)) continue
    if (segmentDist(seg.position.x, seg.position.y, ax, ay, bx, by) > width + seg.renderable.radius) continue
    const chip = damage * T.boss.chain.weaponChipMult
    damageChainSegment(world, seg, chip, events)
    healFromLifesteal(player, chip)
    playSfx('dash_impact', { volume: 0.3, pan: panFor(seg.position.x, player.position.x) })
  }

  // Containers in the corridor take the hit too (B6) — same segment test,
  // no lifesteal (containers.ts owns damage/break so shots share the path).
  for (const box of [...world.with('container', 'position', 'renderable', 'health')]) {
    if (segmentDist(box.position.x, box.position.y, ax, ay, bx, by) > width + box.renderable.radius) continue
    hitContainer(world, box, damage, player, events)
  }

  // Small prey in the corridor gets pulled into an absorb.
  for (const e of [...world.with('consumable', 'position', 'renderable')]) {
    if (e.absorbing || e.recruitable) continue
    if (segmentDist(e.position.x, e.position.y, ax, ay, bx, by) > width + e.renderable.radius) continue
    world.addComponent(e, 'absorbing', {
      progress: 0,
      duration: 0.20,
      startRadius: e.renderable.radius,
      startPos: { x: e.position.x, y: e.position.y },
    })
  }
}
